import React, { useState } from 'react';
import {Box,Flex,Text,IconButton,Icon,Popover,PopoverTrigger,PopoverContent,PopoverBody,VStack,Select,Textarea,Slider,SliderTrack,SliderFilledTrack,SliderThumb,FormControl,FormLabel,Spinner,Image,Tooltip,chakra,useToast} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, ChevronRightIcon, CloseIcon, ArrowUpIcon } from '@chakra-ui/icons';
import { TbMessage2Plus } from "react-icons/tb";
import { FaInfoCircle, FaCog, FaFilePdf, FaFileWord, FaFileExcel, FaFileCsv, FaFile } from 'react-icons/fa';
import ReactTextareaAutosize from 'react-textarea-autosize'; 
import { BsStopFill, BsSend } from 'react-icons/bs';
import { validateImageCount, validateDocumentCount } from '../utils/chat_interface_services';

const ChakraTextareaAutosize = chakra(ReactTextareaAutosize); 

const getDocumentIcon = (filename) => {
  const extension = filename.split('.').pop().toLowerCase();
  switch (extension) {
    case 'pdf':
      return FaFilePdf;
    case 'doc':
    case 'docx':
      return FaFileWord;
    case 'xls':
    case 'xlsx':
      return FaFileExcel;
    case 'csv':
      return FaFileCsv;
    default:
      return FaFile;
  }
};

// Component for displaying info items in popover
const InfoItem = ({ label, value }) => {
  const displayValue = label.toLowerCase() === 'prompt' && typeof value === 'string'
    ? value.replace(/^Formatting re-enabled\n\n/g, '')
    : value;

  return (
    <Box>
      <Text color="#64748b" fontSize="sm" mb={1}>
        {label.toLowerCase() === 'prompt' ? 'System Prompt' : label}
      </Text>
      <Text 
        color="#1f2937" 
        fontSize="sm"
        textAlign="justify"
        lineHeight="1.6"
        {...(label.toLowerCase() === 'prompt' ? {
          maxH: "120px",
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          sx: {
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f5f9',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#cbd5e1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#94a3b8',
            }
          }
        } : {})}
      >{displayValue}</Text>
    </Box>
  );
};

// Model selection component with styled optgroups
const ModelSelector = ({ modelList, selectedModel, onModelSelect }) => (
  <Box position="relative">
    <Select
      bg="#ffffff"
      color="#1f2937"
      borderColor="#e2e8f0"
      _hover={{ borderColor: '#cbd5e1' }}
      onChange={(e) => onModelSelect(e.target.value)}
      value={selectedModel}
      sx={{
        '& optgroup': {
          color: '#64748b',
          fontSize: '12px',
          fontWeight: 'normal',
          bg: '#ffffff',
          textTransform: 'uppercase',
          p: 2,
          mb: 0,
        },
        '& option': {
          color: '#1f2937',
          bg: '#ffffff',
          p: 2,
          pl: 4,
          _hover: {
            bg: '#f1f5f9'
          }
        },
        '& option:checked': {
          bg: '#e2e8f0'
        }
      }}
    >
      {modelList.map((provider) => (
        <optgroup 
          key={provider.provider} 
          label={provider.display_name.toUpperCase()}
        >
          {provider.models.map((model) => (
            <option 
              key={model.id} 
              value={model.id}
              title={model.description}
            >
              {model.display_name}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  </Box>
);

// Advanced settings component with temperature slider
const AdvancedSettings = ({ isOpen, temperature, onTemperatureChange }) => (
  isOpen && (
    <Box mt={2}>
      <Text color="#1f2937" mb={2}>Temperature: {temperature}</Text>
      <Slider
        value={temperature}
        min={0}
        max={2}
        step={0.1}
        onChange={onTemperatureChange}
      >
        <SliderTrack bg="#e2e8f0">
          <SliderFilledTrack bg="#3498db" />
        </SliderTrack>
        <SliderThumb />
      </Slider>
    </Box>
  )
);

// Settings content component with system prompt and advanced settings
const SettingsContent = ({ 
  systemPrompt, 
  onSystemPromptChange, 
  temperature, 
  onTemperatureChange, 
  isAdvancedSettingsOpen, 
  setIsAdvancedSettingsOpen,
  modelList,
  selectedModel,
  onModelSelect
}) => {

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <FormLabel color="#1f2937">Model</FormLabel>
        <ModelSelector 
          modelList={modelList}
          selectedModel={selectedModel}
          onModelSelect={onModelSelect}
        />
      </FormControl>

      <FormControl>
        <FormLabel color="#1f2937">System Prompt</FormLabel>
        <Textarea
          value={systemPrompt} // Use the clean version for display
          onChange={(e) => onSystemPromptChange?.(e.target.value)}
          bg="#ffffff"
          color="#1f2937"
          borderColor="#e2e8f0"
          _hover={{ borderColor: '#cbd5e1' }}
          rows={3}
        />
      </FormControl>

      <Box>
        <Flex 
          onClick={() => setIsAdvancedSettingsOpen(!isAdvancedSettingsOpen)}
          cursor="pointer"
          align="center"
          color="#1f2937"
          py={2}
          _hover={{ opacity: 0.8 }}
        >
          <Text mr={1}>Advanced Settings</Text>
          <Icon 
            as={isAdvancedSettingsOpen ? ChevronDownIcon : ChevronRightIcon}
            boxSize="16px"
          />
        </Flex>
        
        <AdvancedSettings 
          isOpen={isAdvancedSettingsOpen}
          temperature={temperature}
          onTemperatureChange={onTemperatureChange}
        />
      </Box>
    </VStack>
  );
};

// Chat info popover component
const ChatInfoPopover = ({ info }) => (
  <Popover trigger="hover" placement="bottom-end" isLazy>
    <PopoverTrigger>
      <IconButton
        icon={<Icon as={FaInfoCircle} boxSize="20px" />}
        variant="ghost"
        color="#1f2937"
        size="sm"
        _hover={{ bg: 'transparent', color: '#3498db' }}
        aria-label="Information"
      />
    </PopoverTrigger>
    <PopoverContent bg="#ffffff" borderColor="#e2e8f0" p={4} w="400px" _focus={{ boxShadow: 'none' }}>
      <PopoverBody p={0}>
        <Text color="#1f2937" fontSize="xl" fontWeight="medium" mb={4}>Info</Text>
        <VStack align="stretch" spacing={3}>
          {Object.entries(info).map(([key, value]) => (
            <InfoItem key={key} label={key} value={value} isPrompt={key.toLowerCase() === 'prompt'} />
          ))}
        </VStack>
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

// Function to get default system prompt with current date
const getDefaultSystemPrompt = () => {
  const currentDate = new Date().toLocaleDateString('en-GB');
  return `You are a helpful, friendly assistant designed to provide accurate, concise, and relevant responses. Today is ${currentDate}. Follow the user's request closely. Do not assume a specific domain or document context unless the user explicitly asks for it. For simple greetings, reply briefly and naturally. Mention sources or document names only when you actually used retrieved document context or when the user asks for citations.`;
};

// Main header component
export const Header = ({ 
  title, 
  info, 
  isNewChat = false, 
  modelList = [], 
  onModelSelect,
  systemPrompt = getDefaultSystemPrompt(),
  onSystemPromptChange,
  temperature = 0.5,
  onTemperatureChange,
  selectedModel = "",
  onNewChat,
}) => {
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);
  const displaySystemPrompt = systemPrompt ?
    systemPrompt.replace(/^Formatting re-enabled\n\n/g, '') : 
    getDefaultSystemPrompt();

  return (
    <Flex
      py={3}
      px={6}
      minH="60px"
      borderBottom="1px"
      borderColor="#3498db"
      justify="space-between"
      align="center"
      bg="#ffffff"
    >
      <Box flex={1} />
      <Text color="#1f2937" fontWeight="medium" fontSize="md">
        {!isNewChat ? title : ''}
      </Text>
      <Flex flex={1} justify="flex-end" align="center" gap={2}>
        {!isNewChat && info && <ChatInfoPopover info={info} />}
        {isNewChat ? (
          <Flex align="center" gap={2}>
            <Text color="#1f2937" fontSize="sm" fontWeight="medium">
              {modelList.find(p => 
                p.models.some(m => m.id === selectedModel)
              )?.models.find(m => 
                m.id === selectedModel
              )?.display_name || 'Select Model'}
            </Text>
            <Popover placement="bottom-end">
              <PopoverTrigger>
                <IconButton
                  icon={<Icon as={FaCog} boxSize="20px" />}
                  variant="ghost"
                  color="#1f2937"
                  size="sm"
                  _hover={{ bg: 'transparent', color: '#3498db' }}
                  aria-label="Model Settings"
                />
              </PopoverTrigger>
              <PopoverContent bg="#ffffff" borderColor="#e2e8f0" p={4} w="400px">
                <PopoverBody>
                  <SettingsContent 
                    systemPrompt={displaySystemPrompt}
                    onSystemPromptChange={onSystemPromptChange}
                    temperature={temperature}
                    onTemperatureChange={onTemperatureChange}
                    isAdvancedSettingsOpen={isAdvancedSettingsOpen}
                    setIsAdvancedSettingsOpen={setIsAdvancedSettingsOpen}
                    modelList={modelList}
                    selectedModel={selectedModel}
                    onModelSelect={onModelSelect}
                  />
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Flex>
        ) : (
          <Tooltip 
            label="Start a new chat" 
            hasArrow 
            placement="bottom-start"
          >
            <IconButton
              icon={<Icon as={TbMessage2Plus} boxSize="20px" />}
              variant="ghost"
              color="#1f2937"
              size="sm"
              _hover={{ bg: 'transparent', color: '#3498db' }}
              aria-label="New item"
              onClick={onNewChat}
            />
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};

// Reusable Input Component
export const InputBox = ({ 
  placeholder = "Type your message...",
  value = "",
  onChange,
  onSubmit,
  onStop,
  isDisabled = false,
  hideFileUpload = false,
  modelCapabilities = {
    supports_image: false,
    supports_documents: false,
    max_file_size: 0,
    supported_file_formats: []
  },
  messages = [],
  provider = "",
  isStreaming = false
}) => {
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const fileInputRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const toast = useToast();
  const [isDragging, setIsDragging] = useState(false);
  
  const handlePaste = (e) => {
    if (isDisabled || hideFileUpload) return;
    const items = e.clipboardData?.items;
    const imageItems = Array.from(items).filter(item => item.type.indexOf('image') !== -1);
    
    if (!validateImageCount({
      currentImagesCount: images.length, 
      newImagesCount: imageItems.length,
      toast
    })) {
      return;
    }
    
    imageItems.forEach(item => {
      const blob = item.getAsFile();
      setImages(prev => [...prev, blob]);
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled && !isStreaming && !hideFileUpload) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isDisabled || isStreaming || hideFileUpload) return;

    // Count how many image files are being dropped
    const droppedFiles = Array.from(e.dataTransfer.files);
    const imageFiles = droppedFiles.filter(file => 
      file.type.startsWith('image/') && 
      !(file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg'))
    );
    
    // Count how many document files are being dropped (non-image files)
    const documentFiles = droppedFiles.filter(file => 
      !file.type.startsWith('image/')
    );
    
    // Validate image count
    if (!validateImageCount({
      currentImagesCount: images.length, 
      newImagesCount: imageFiles.length,
      toast
    })) {
      return;
    }
    
    // Validate document count
    if (!validateDocumentCount({
      currentDocumentsCount: documents.length,
      newDocumentsCount: documentFiles.length,
      toast
    })) {
      return;
    }

    const dataTransfer = new DataTransfer();
    Array.from(e.dataTransfer.files).forEach(file => {
      dataTransfer.items.add(file);
    });

    if (fileInputRef.current) {
      fileInputRef.current.files = dataTransfer.files;
      const event = new Event('change', { bubbles: true });
      fileInputRef.current.dispatchEvent(event);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    
    // Count how many images we're trying to add
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/') && 
      !(file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg'))
    );
    
    const documentFiles = files.filter(file => 
      !file.type.startsWith('image/')
    );
    
    if (!validateImageCount({
      currentImagesCount: images.length, 
      newImagesCount: imageFiles.length,
      toast
    })) {
      e.target.value = '';
      return;
    }
    
    // Validate document count
    if (!validateDocumentCount({
      currentDocumentsCount: documents.length,
      newDocumentsCount: documentFiles.length,
      toast
    })) {
      e.target.value = '';
      return;
    }

    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
      
      if (isImage && !isSvg) {
        setImages(prev => [...prev, file]);
      } else if (isSvg) {
        toast({
          title: 'SVG Not Supported',
          description: 'SVG image format is not supported',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        setDocuments(prev => [...prev, file]);
      }
    }

    e.target.value = '';
  };

  const removeImage = (index) => {
    if (isDisabled) return;
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = (index) => {
    if (isDisabled) return;
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (isDisabled) return;
    if (hideFileUpload) {
      onSubmit();
    } else {
      onSubmit(images, documents, value);
      setImages([]);
      setDocuments([]);
    }
    textareaRef.current?.blur();
  };

  const handleUploadClick = () => {
    if (modelCapabilities.supports_documents === false && modelCapabilities.supports_image === false) {
      toast({
        title: 'File uploads not supported',
        description: 'The selected model does not support file uploads',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const hasImagesInHistory = messages.some(msg => {
      if (Array.isArray(msg.content)) {
        return msg.content.some(item => 
          item.type === 'image_url' || item.type === 'image'
        );
      }
      return msg.images?.length > 0;
    });

    const hasDocumentsInHistory = messages.some(msg => {
      if (msg.document_names?.length > 0 || msg.documents?.length > 0) {
        return true;
      }
      if (typeof msg.content === 'string' && msg.content.includes('[DOCUMENT_REFERENCES:{')) {
        return true;
      }
      return false;
    });

    const hasCurrentImages = images.length > 0;
    const hasCurrentDocuments = documents.length > 0;

    let acceptTypes = [];
    
    if (provider === 'openai') {
      if (hasCurrentDocuments || hasDocumentsInHistory) {
        if (!modelCapabilities.supports_documents) {
          toast({
            title: 'Cannot upload more files',
            description: 'You can only upload documents in this conversation',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        acceptTypes = modelCapabilities.supported_file_formats
          .filter(format => !['jpg', 'jpeg', 'png', 'gif'].includes(format.toLowerCase()))
          .map(format => `.${format}`);
      }
      else if (hasCurrentImages || hasImagesInHistory) {
        if (!modelCapabilities.supports_image) {
          toast({
            title: 'Cannot upload more files',
            description: 'You can only upload images in this conversation',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        acceptTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].map(type => `.${type.split('/')[1]}`);
      }
      else {
        if (modelCapabilities.supports_image) {
          acceptTypes.push(...['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].map(type => `.${type.split('/')[1]}`));
        }
        if (modelCapabilities.supports_documents) {
          const documentTypes = modelCapabilities.supported_file_formats
            .filter(format => !['jpg', 'jpeg', 'png', 'gif'].includes(format.toLowerCase()))
            .map(format => `.${format}`);
          acceptTypes.push(...documentTypes);
        }
      }
    } else {
      if (modelCapabilities.supports_image) {
        acceptTypes.push(...['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].map(type => `.${type.split('/')[1]}`));
      }
      if (modelCapabilities.supports_documents) {
        const documentTypes = modelCapabilities.supported_file_formats.map(format => `.${format}`);
        acceptTypes.push(...documentTypes);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.accept = acceptTypes.join(',');
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  return (
    <Box 
      maxW="4xl" 
      w={{ base: "100%", md: "min(90%, 62rem)" }}
      mx="auto" 
      px={{ base: 4, md: 6 }} 
      py={{ base: 4, md: 8 }}
      position="sticky"
      bottom="0"
      bg="#ffffff"
      zIndex={100}
    >
      {!hideFileUpload && (images.length > 0 || documents.length > 0) && (
        <Flex gap={2} mb={2} flexWrap="wrap">
          {images.map((image, index) => (
            <Box key={`img-${index}`} position="relative">
              <Image 
                src={URL.createObjectURL(image)} 
                alt="Pasted image"
                boxSize="30px"
                objectFit="cover"
                borderRadius="sm"
              />
              <IconButton
                icon={<CloseIcon boxSize="8px" />}
                size="xs"
                position="absolute"
                top="-2"
                right="-2"
                borderRadius="full"
                bg="gray.700"
                _hover={{ bg: 'gray.600' }}
                onClick={() => removeImage(index)}
                isDisabled={isDisabled}
              />
            </Box>
          ))}
          {documents.map((doc, index) => (
            <Flex 
              key={`doc-${index}`}
              alignItems="center" 
              bg="#e2e8f0" 
              p={2} 
              borderRadius="md"
              position="relative"
            >
              <Icon 
                as={getDocumentIcon(doc.name)}
                boxSize="16px"
                mr={2}
                color="gray.400"
              />
              <Text fontSize="sm" color="#1f2937">{doc.name}</Text>
              <IconButton
                icon={<CloseIcon boxSize="8px" />}
                size="xs"
                position="absolute"
                top="-2"
                right="-2"
                borderRadius="full"
                bg="gray.700"
                _hover={{ bg: 'gray.600' }}
                onClick={() => removeDocument(index)}
                isDisabled={isDisabled}
              />
            </Flex>
          ))}
        </Flex>
      )}
      <Flex
        position="relative" 
        w="full" 
        align="center"
        opacity={isDisabled || isStreaming ? 0.6 : 1}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        bg={isDragging ? "rgba(52, 152, 219, 0.08)" : undefined}
        transition="background-color 0.2s"
        borderRadius="16px"
      >
        {!hideFileUpload && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              multiple
              disabled={isDisabled || isStreaming}
            />
            <IconButton
              icon={<AddIcon boxSize="12px" />}
              color="white"     
              bg="#3498db"      
              opacity={1}    
              size="sm"
              position="absolute"
              left={6}         
              zIndex={2}
              borderRadius="full"
              border="none" 
              _hover={{ 
                bg: '#2e86c1',
                borderColor: '#2e86c1' 
              }}
              aria-label="Add content"
              onClick={handleUploadClick}
              isDisabled={isDisabled || isStreaming}
            />
          </>
        )}
        <ChakraTextareaAutosize
          ref={textareaRef}
          value={value}
          onChange={(e) => !isDisabled && !isStreaming && onChange(e)}
          placeholder={placeholder}
          onPaste={(e) => !isDisabled && !isStreaming && handlePaste(e)}
          onKeyDown={(e) => {
            if (isDisabled || isStreaming) {
              e.preventDefault();
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (value.trim() || images.length > 0 || documents.length > 0) {
                handleSubmit();
              }
            }
          }}
          minRows={1}
          maxRows={6}
          pl={20}
          pr={20}
          py={5}
          minH="54px"
          color="#1f2937"
          _placeholder={{ color: '#94a3b8' }}
          width="full"
          resize="none"
          bg="#ffffff"
          outline="none"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#cbd5e1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#94a3b8',
            },
          }}
          border="1px solid"
          borderColor="#e2e8f0"
          borderRadius="16px"
          boxShadow="0 2px 8px rgba(15, 23, 42, 0.08)"
          _hover={{ 
            borderColor: '#cbd5e1',
            bg: '#f8fafc'
          }}
          _focus={{ 
            borderColor: '#3498db',
            boxShadow: '0 0 0 1px #3498db',
            bg: '#ffffff'
          }}
          fontSize="sm"
          lineHeight="1.5"
          disabled={isDisabled || isStreaming}
          opacity={isDisabled || isStreaming ? 0.6 : 1}
          pointerEvents={isDisabled || isStreaming ? 'none' : 'auto'}
          _disabled={{
            cursor: 'not-allowed',
            opacity: 0.6,
            bg: '#f8fafc'
          }}
        />
        <IconButton
          icon={isStreaming ? <Icon as={BsStopFill} boxSize="16px" /> : <Icon as={BsSend} boxSize="16px" />}
          color="white"
          bg="#3498db"
          opacity={isStreaming || value.trim() || images.length > 0 || documents.length > 0 ? 1 : 0.5}
          size="sm"
          position="absolute"
          right={6}
          zIndex={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="full"
          border="none"
          _hover={{ bg: isStreaming || value.trim() || images.length > 0 || documents.length > 0 ? '#2e86c1' : '#3498db' }}
          onClick={isStreaming ? onStop : handleSubmit}
          aria-label={isStreaming ? "Stop streaming" : "Send"}
          isDisabled={isDisabled || (!isStreaming && !value.trim() && images.length === 0 && documents.length === 0)}
        />
      </Flex>
    </Box>
  );
};

// Loading Overlay Component
export const LoadingOverlay = () => (
  <Flex
    position="fixed"
    top={0}
    left={0}
    right={0}
    bottom={0}
    bg="blackAlpha.700"
    zIndex={1000}
    justify="center"
    align="center"
    backdropFilter="blur(4px)"
  >
    <Spinner
      thickness="4px"
      speed="0.65s"
      emptyColor="gray.200"
      color="blue.500"
      size="xl"
    />
  </Flex>
); 
